import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Tag, Select, Button, Progress, Tooltip, Typography, Space, notification } from 'antd';
import { SafetyOutlined, ReloadOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';

const { Text } = Typography;

/**
 * Plan-model check panel for the PA review screen (new plan-management flow,
 * dark launch). Renders NOTHING unless:
 *   - the viewer holds plan_designer.manage, AND
 *   - the enrollee's plan has pm_enforcement_state.mode = log|enforce.
 * In `log` mode everything here is advisory — nothing blocks; `enforce` mode
 * mirrors what the backend gate will actually reject.
 *
 * Data: GET /v2/admin/pm/pa-panel?checkin_code=…  (lib.pmPaPanel — silent on
 * any failure). Mapping: POST /v2/admin/pm/pa/map (line-only stamping —
 * facility-level tariff mapping continues via the tagging queue).
 */

const VERDICT_TAG = {
  resolved: { color: 'green', label: 'Resolved' },
  mapping_required: { color: 'gold', label: 'Mapping required' },
  not_covered: { color: 'red', label: 'Not covered' },
  unclassified: { color: 'default', label: 'Unclassified' },
};

const fmtMoney = (v) => `₦${Number(v || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;

const fmtPoolValue = (pool, v) => {
  if (v === null || v === undefined) return '—';
  if (pool.metric === 'money') return fmtMoney(v);
  const unit = pool.metric === 'days' ? 'days' : pool.metric === 'quantity' ? 'units' : 'times';
  return `${Number(v)} ${unit}`;
};

const windowLabel = (pool) => {
  const w = pool?.window || {};
  if (w.kind === 'calendar_month') return 'this month';
  if (w.kind === 'rolling_months') return `rolling ${w.months} mo`;
  return 'policy year';
};

function PoolRow({ pool }) {
  const limit = pool.limit_value;
  const consumed = pool.consumed;
  const after = pool.after_proposed;
  const pct = limit && consumed !== null ? Math.min(100, Math.round((consumed / limit) * 100)) : null;
  const afterPct = limit && after !== null ? Math.min(100, Math.round((after / limit) * 100)) : null;
  const breach = !!pool.would_breach;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: pool.name }}>
          {pool.kind === 'envelope' ? <b>{pool.name}</b> : pool.name}
        </Text>
        <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{windowLabel(pool)}</Text>
      </div>
      {limit === null ? (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {fmtPoolValue(pool, consumed)} used · unlimited
        </Text>
      ) : (
        <Tooltip
          title={
            pool.proposed_delta
              ? `${fmtPoolValue(pool, consumed)} used, +${fmtPoolValue(pool, pool.proposed_delta)} pending → ${fmtPoolValue(pool, after)} of ${fmtPoolValue(pool, limit)}`
              : `${fmtPoolValue(pool, consumed)} of ${fmtPoolValue(pool, limit)}`
          }
        >
          <div>
            <Progress
              percent={afterPct ?? pct ?? 0}
              success={pct !== null && afterPct !== null && afterPct > pct ? { percent: pct } : undefined}
              size="small"
              status={breach ? 'exception' : (pct ?? 0) >= 80 ? 'active' : 'normal'}
              showInfo={false}
            />
            <Text type={breach ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
              {fmtPoolValue(pool, after ?? consumed)} of {fmtPoolValue(pool, limit)}
              {breach ? ' — would exceed limit' : ''}
            </Text>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

function MappingLine({ line, onMapped }) {
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    if (!choice) return;
    setSaving(true);
    const res = await lib.pmPaMap({
      benefit_item_id: choice,
      claim_item_ids: [line.claim_item_id],
    });
    setSaving(false);
    if (res?.error) {
      notification.error({ message: res.message || 'Mapping failed' });
      return;
    }
    notification.success({ message: 'Mapped', description: res?.message });
    onMapped();
  };

  return (
    // Plain flex row so a long benefit name can't push the Map button away.
    <div style={{ display: 'flex', gap: 4, marginTop: 4, width: '100%', minWidth: 0 }}>
      <Select
        size="small"
        style={{ flex: '1 1 0', minWidth: 0 }}
        placeholder="Pick benefit…"
        value={choice}
        onChange={setChoice}
        popupMatchSelectWidth={false}
        dropdownStyle={{ minWidth: 320, maxWidth: 'min(520px, 90vw)' }}
        optionRender={(opt) => (
          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{opt.label}</span>
        )}
        optionLabelProp="label"
        options={(line.candidates || []).map((c) => ({
          value: c.benefit_item_id,
          label: c.name,
          title: c.name,
        }))}
      />
      <Button size="small" type="primary" style={{ flex: '0 0 auto' }} loading={saving} disabled={!choice} onClick={apply}>
        Map
      </Button>
    </div>
  );
}

/**
 * checkSummaryOnly: render the enforcement check (mode, would-block, not
 * covered, mapping still needed) without pool rows or mapping pickers — for
 * pages that also show PmMappingPanel, which carries the full consumption
 * breakdown and the mapping controls.
 */
export default function PmPaPanel({ checkinCode, refreshKey = 0, lineLabels = null, checkSummaryOnly = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!checkinCode) return;
    setLoading(true);
    const payload = await lib.pmPaPanel(checkinCode);
    setData(payload);
    setLoading(false);
  }, [checkinCode]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Only lines that genuinely need a mapping decision are listed (mapping
  // finding #2). Lines that resolved, are not covered, or cannot be
  // classified are summarised in one line instead.
  const interestingLines = useMemo(
    () => (data?.lines || []).filter((l) => l.verdict === 'mapping_required'),
    [data]
  );
  const resolvedCount = useMemo(
    () => (data?.lines || []).filter((l) => l.verdict === 'resolved').length,
    [data]
  );
  const notCovered = useMemo(
    () => (data?.lines || []).filter((l) => l.verdict === 'not_covered'),
    [data]
  );

  // Dark by default: no data, or plan not in the parallel run → render nothing.
  if (!data || data.mode === 'ignore' || data.mode === 'off' || !data.mode) return null;

  const enforce = data.mode === 'enforce_strict' || data.mode === 'enforce';
  const summary = data.summary || {};

  return (
    <Card
      size="small"
      style={{ width: '100%' }}
      title={
        <Space size={6}>
          <SafetyOutlined />
          <span style={{ fontSize: 13 }}>Plan model check</span>
          <Tag color={enforce ? 'red' : 'gold'} style={{ marginInlineEnd: 0 }}>
            {enforce ? 'ENFORCING STRICTLY' : 'ADVISORY'}
          </Tag>
        </Space>
      }
      extra={<Button size="small" type="text" icon={<ReloadOutlined />} loading={loading} onClick={load} />}
    >
      {/* The PA aside is sticky and can exceed the viewport; without its own
          scroll region the mapping controls at the bottom become unreachable. */}
      <div style={{ maxHeight: '52vh', overflowY: 'auto', overflowX: 'hidden' }}>
      {!enforce && summary.would_block ? (
        <Text type="warning" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Would block under enforcement — advisory only for now.
        </Text>
      ) : null}

      {(checkSummaryOnly ? [] : (data.pools || []))
        .filter((pool) =>
          // Only what THIS request draws from (stakeholder suggestion #10):
          // the encounter's own envelope, plus any pool the pending lines
          // move or would breach. The full breakdown stays in Consumption.
          (pool.kind === 'envelope'
            && (pool.checkin_type_id === null || Number(pool.checkin_type_id) === Number(data.checkin_type_id)))
          || Number(pool.proposed_delta) > 0
          || pool.would_breach
        )
        .map((pool) => (
          <PoolRow key={pool.pool_id} pool={pool} />
        ))}

      {resolvedCount > 0 ? (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
          {resolvedCount} line{resolvedCount === 1 ? '' : 's'} resolved automatically.
        </Text>
      ) : null}
      {notCovered.length > 0 ? (
        <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
          {enforce ? 'Will be blocked' : 'Not covered on this plan'}:{' '}
          {notCovered.map((l) => {
            const label = lineLabels?.[l.claim_item_id];
            return label ? `#${label.index} ${label.name}` : (l.item_name || `line #${l.claim_item_id}`);
          }).join(', ')}
        </Text>
      ) : null}
      {interestingLines.length > 0 ? (
        <Text strong style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
          {interestingLines.length} line{interestingLines.length === 1 ? '' : 's'} need a mapping decision{checkSummaryOnly ? ' — map them in Consumption & benefit mapping.' : ' — mapped once, reused at every facility:'}
        </Text>
      ) : null}

      {(checkSummaryOnly ? [] : interestingLines).map((line) => {
        const tag = VERDICT_TAG[line.verdict] || VERDICT_TAG.unclassified;
        const label = lineLabels?.[line.claim_item_id];
        return (
          <div key={line.claim_item_id} style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
            <Space size={6} wrap>
              {label ? (
                <Tag style={{ marginInlineEnd: 0, fontVariantNumeric: 'tabular-nums' }}>#{label.index}</Tag>
              ) : null}
              <Tag color={tag.color} style={{ marginInlineEnd: 0 }}>{tag.label}</Tag>
              <Text style={{ fontSize: 12 }} strong={!!label}>
                {label?.name || `line #${line.claim_item_id}`}
              </Text>
              {line.benefit_item_name ? (
                <Text type="secondary" style={{ fontSize: 11, wordBreak: 'break-word' }}>→ {line.benefit_item_name}</Text>
              ) : null}
            </Space>
            {line.verdict === 'mapping_required' ? (
              <MappingLine line={line} onMapped={load} />
            ) : null}
            {line.verdict === 'not_covered' ? (
              <Text type="danger" style={{ fontSize: 11, display: 'block' }}>
                {enforce ? 'Approval will be blocked for this line.' : 'Not covered on this plan (advisory).'}
              </Text>
            ) : null}
          </div>
        );
      })}
      </div>
    </Card>
  );
}
